import {
  AccountRegisterStatus,
  type AuthPayload,
  type LoginInput,
  type UserRegisterInput,
} from "~/layers/admin/modules/auth/runtime/types/api";
import { useToast } from "vue-toastification";
import { createHooks } from "hookable";
import { useApolloClient } from "@vue/apollo-composable";
import gql from "graphql-tag";

interface EventNames {
  "register:success": { (status: AccountRegisterStatus): void };
  "register:error": { (error: Error): void };
  "register:must-verify-email": { (email: string): void };
  "login:new-user": { (auth: AuthPayload): void };
}

interface SignupIntent {
  token: string;
  email: string;
  name: string;
  intent: string;
}

const events = createHooks<EventNames>();

export const useSellerAuth = () => {
  const auth = useAuth();
  const toast = useToast();
  const apollo = useApolloClient();
  const apolloClient = apollo.resolveClient();

  const error = ref(false);
  const registering = ref(false);
  const checkingEmail = ref(false);
  const fetchingIntent = ref(false);
  const loggingIn = ref(false);

  const loading = computed(
    () => registering.value || checkingEmail.value || loggingIn.value
  );
  const inputsDisabled = computed(() => loading.value || fetchingIntent.value);

  const socialLogins = [
    {
      name: "Google",
      provider: "google",
      icon: "logos:google-icon",
    },
    {
      name: "Apple",
      provider: "apple",
      icon: "logos:apple",
    },
    {
      name: "Facebook",
      provider: "facebook",
      icon: "logos:facebook",
    },
  ];

  const login = (credentials: LoginInput) => {
    loggingIn.value = true;
    auth
      .login(credentials)
      .then((result) => {
        error.value = false;

        // Redirect to onboarding right after login if
        // there is no workspace. We need this before any other
        // invalid workspace API calls.
        if (result?.context && !result.context.workspace) {
          events.callHook("login:new-user", result);
        }
      })
      .catch(() => {
        error.value = true;
      })
      .finally(() => {
        loggingIn.value = false;
      });
  };

  const register = (registration?: UserRegisterInput) => {
    if (registration) {
      registering.value = true;
      const input = toRaw(registration);
      auth
        .register(input)
        .then((status) => {
          error.value = false;
          if (status) {
            if (status === AccountRegisterStatus.MUST_VERIFY_EMAIL) {
              events.callHook("register:must-verify-email", registration.email);
            } else {
              events.callHook("register:success", status);
            }
          } else {
            events.callHook(
              "register:error",
              new Error("Error creating account")
            );
          }
        })
        .catch(() => {
          error.value = true;
          toast.error("Error creating account");
        })
        .finally(() => {
          registering.value = false;
        });
    }
  };

  const socialLogin = (provider: string, originalUrl?: string) => {
    try {
      if (!originalUrl) {
        const route = useRoute();
        const redirectQuery = route.query.redirect;
        originalUrl = Array.isArray(redirectQuery)
          ? redirectQuery[0] || "/"
          : redirectQuery || "/";
      }
      auth.socialLoginRedirect(provider, { originalUrl });
    } catch (error) {
      console.error("Social login error:", error);
    }
  };

  const checkEmail = (email: string) => {
    return new Promise<boolean>((resolve) => {
      checkingEmail.value = true;
      apolloClient
        .query<{ checkSignupEmail: boolean }>({
          query: gql`
            query ($email: String!) {
              checkSignupEmail(email: $email)
            }
          `,
          variables: {
            email,
          },
        })
        .then(({ data }) => {
          error.value = false;
          resolve(data.checkSignupEmail);
        })
        .catch((error) => {
          error.value = true;
          console.error("Error checking email:", error);
        })
        .finally(() => {
          checkingEmail.value = false;
        });
    });
  };

  const fetchIntent = (intent: string) => {
    return new Promise<SignupIntent>((resolve) => {
      fetchingIntent.value = true;
      apolloClient
        .query<{ signupIntent: SignupIntent }>({
          query: gql`
            query ($where: SignupIntentWhereUniqueInput!) {
              signupIntent(where: $where) {
                token
                email
                name
              }
            }
          `,
          variables: {
            where: { token: intent },
          },
          fetchPolicy: "no-cache",
        })
        .then(({ data }) => {
          error.value = false;
          resolve(data.signupIntent);
        })
        .catch((error) => {
          error.value = true;
          console.error("Error fetching intent:", error);
        })
        .finally(() => {
          fetchingIntent.value = false;
        });
    });
  };

  const onEvent = <E extends keyof EventNames>(event: E, fn: EventNames[E]) => {
    events.hook(event, fn as any);
  };

  return {
    error,
    loading,
    inputsDisabled,
    login,
    register,
    socialLogins,
    socialLogin,
    checkEmail,
    fetchIntent,
    onEvent,
  };
};
